import { PERSPECTIVE_BUCKET } from './domain.js';
import { countsInAggregates, countsInHeadcount, fxSalary, loadedCost } from './cost.js';
import { currentBucket } from './decisions.js';
import { availableFields, getEmpField, isTargetLevelKey, passesFilters, resolveField, resolvedMetricFn, } from './fields.js';
import { buildHierarchy, groupNodeId, ROOT_ID, } from './hierarchy.js';
import { computeOneOffCosts, computeStageBridge, computeStrandedTimeline, FTE, salaryMetric, } from './economics.js';
/**
 * Layer depths and management spans for one perspective and stage.
 *
 * Takes the lens explicitly instead of temporarily mutating shared state — the original's
 * save/restore pattern is not re-entrant, so two overlapping calls could each observe the other's
 * lens. Spans/layers default to the Day 1 org unless a stage is asked for.
 */
/**
 * Spans, layers and outliers for one lens.
 *
 * Takes the WHOLE lens, not a few named fields. It used to take `{ perspective, stage?, filters?,
 * lens?: Partial<ViewLens> }` with a whole-object default of `{ perspective: 'as-is' }` — so
 * `computeSpansLayers(state)` compiled and quietly answered about the unfiltered as-is org, and
 * `viewLayout`, `viewGrouping` and `targetView` could be dropped without a compile error. The function's
 * own promise is "the population ON SCREEN", and a spans pane opened beside a reporting-line chart or a
 * custom pivot would have described a different organisation with nothing saying so.
 *
 * `viewMode` is the one field it overrides: structure is measured on the in-scope org, so out-of-scope
 * people are removed rather than dimmed. That is faithful — the shipped app forces the same.
 */
export function computeSpansLayers(state, lensIn) {
    const lens = { ...lensIn, viewMode: 'hide' };
    const flat = buildHierarchy(state, lens);
    const byId = new Map();
    for (const n of flat)
        byId.set(n.id, { node: n, children: [] });
    for (const n of flat) {
        if (!n.parentId)
            continue;
        byId.get(n.parentId)?.children.push(byId.get(n.id));
    }
    const rootNode = flat.find((n) => !n.parentId);
    const layers = [];
    if (rootNode) {
        // Iterative, so a deep chain cannot blow the stack.
        const stack = [{ w: byId.get(rootNode.id), depth: 0 }];
        while (stack.length) {
            const { w, depth } = stack.pop();
            layers.push({ node: w, depth });
            for (let i = w.children.length - 1; i >= 0; i--)
                stack.push({ w: w.children[i], depth: depth + 1 });
        }
    }
    const layerCount = {};
    for (const { depth } of layers)
        layerCount[depth] = (layerCount[depth] ?? 0) + 1;
    const layerHisto = Object.keys(layerCount)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => [k, layerCount[Number(k)]]);
    // Groups are attributed to the second hierarchy level by default — the level that reads as
    // "function" in most client structures.
    const breakdownLevel = state.groupingLevels[Math.min(1, state.groupingLevels.length - 1)];
    const breakdownKey = breakdownLevel ? breakdownLevel.key : null;
    const levelValueFor = (w) => {
        if (!breakdownKey)
            return '';
        const n = w.node;
        if (n.kind === 'person') {
            const v = getEmpField(n.data, breakdownKey, 'as-is', undefined);
            return v == null ? '' : String(v);
        }
        if (n.kind === 'group') {
            const entry = n.data.path.find((p) => p.levelKey === breakdownKey);
            return entry ? entry.value : '';
        }
        return '';
    };
    const parents = layers
        .filter((l) => l.node.children.length > 0)
        .map((l) => {
        const n = l.node.node;
        const name = n.kind === 'person'
            ? n.data.name || n.label
            : n.kind === 'group'
                ? n.data.head?.name || n.label
                : n.label;
        return { name, levelValue: levelValueFor(l.node), span: l.node.children.length, kind: n.kind };
    });
    const spanCount = {};
    for (const p of parents)
        spanCount[p.span] = (spanCount[p.span] ?? 0) + 1;
    const spanHisto = Object.keys(spanCount)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => [k, spanCount[Number(k)]]);
    const totalParents = parents.length;
    const groupMap = new Map();
    for (const { node, depth } of layers) {
        const v = levelValueFor(node);
        if (!v)
            continue;
        let entry = groupMap.get(v);
        if (!entry) {
            entry = { depths: [], spans: [] };
            groupMap.set(v, entry);
        }
        entry.depths.push(depth);
        if (node.children.length)
            entry.spans.push(node.children.length);
    }
    const byFunction = [...groupMap.entries()]
        .map(([name, v]) => ({
        name,
        avgLayer: v.depths.reduce((a, b) => a + b, 0) / (v.depths.length || 1),
        maxSpan: v.spans.length ? Math.max(...v.spans) : 0,
        minSpan: v.spans.length ? Math.min(...v.spans) : 0,
        low: v.spans.filter((s) => s < 3).length,
        high: v.spans.filter((s) => s > 8).length,
    }))
        .sort((a, b) => b.maxSpan - a.maxSpan);
    return {
        layerHisto,
        spanHisto,
        maxLayer: layerHisto.length ? Math.max(...layerHisto.map(([k]) => Number(k))) : 0,
        avgSpan: totalParents ? parents.reduce((a, p) => a + p.span, 0) / totalParents : 0,
        lowSpan: parents.filter((p) => p.span < 3 && p.kind !== 'root'),
        highSpan: parents.filter((p) => p.span > 8),
        byFunction,
        // Headcount excludes unfilled positions, inactive people and leavers; org *structure* still
        // counts every node in view.
        count: layers.filter((l) => l.node.node.kind === 'person' && countsInAggregates(l.node.node.data)).length,
        breakdownLabel: breakdownLevel ? breakdownLevel.label : 'Group',
    };
}
/**
 * Classify the effect of a dedication threshold WITHOUT touching state.
 *
 * The rule governs only people with no explicit decision plus its own previous picks
 * (`src: 'rule'`). An explicit Remain / Carve-out / No-longer-required assignment is a human
 * decision and the slider never moves it — that asymmetry is the whole reason the rule is safe to
 * drag around live.
 */
export function previewPerimeterThreshold(state, threshold, options = {}) {
    const design = options.design ?? state.design;
    const r = {
        threshold,
        hc: 0, fte: 0, dedicatedFte: 0, cost: 0, members: 0,
        enterIds: [], exitIds: [],
        manualIn: 0, manualOut: 0,
        missingDedication: 0, totalHc: 0,
    };
    for (const p of state.employees) {
        if (p.isLeaver)
            continue;
        const counts = countsInHeadcount(p);
        if (counts)
            r.totalHc++;
        const cur = design.buckets[p.id]; // undefined = undecided
        const isRule = cur === 'Carve-out' && (design.bucketMeta[p.id] ?? {}).src === 'rule';
        const d = p.dedication;
        if (d == null && cur !== 'No longer required')
            r.missingDedication++;
        const qualifies = threshold != null && d != null && d >= threshold;
        let member = false;
        if (cur === 'No longer required') {
            if (qualifies)
                r.manualOut++;
        }
        else if (cur === 'Carve-out' && !isRule) {
            // Only PERIMETER members belong to this preview: a Day 1 transfer is a phase-3 decision and
            // is counted there, not in the perimeter.
            const joinStage = (design.bucketMeta[p.id] ?? {}).joinStage || 'perimeter';
            if (joinStage === 'perimeter') {
                member = true;
                if (!qualifies)
                    r.manualIn++;
            }
        }
        else if (cur === 'Remain') {
            if (qualifies)
                r.manualOut++;
        }
        else if (isRule) {
            if (qualifies)
                member = true;
            else
                r.exitIds.push(p.id);
        }
        else {
            if (qualifies) {
                member = true;
                r.enterIds.push(p.id);
            }
        }
        if (member) {
            r.members++;
            if (counts) {
                r.hc++;
                const fte = Number(p.fte) || 0;
                r.fte += fte;
                if (d != null)
                    r.dedicatedFte += fte * d;
                r.cost += fxSalary(p, state.assumptions);
            }
        }
    }
    return r;
}
/** Ribbons narrower than this are noise on any chart and are dropped. */
const SANKEY_EPSILON = 0.004;
/**
 * The as-is → Day 1 → Day 2 population flow, derived purely from a bridge result — which is what
 * makes it conserve by construction rather than by agreement.
 */
export function separationSankeyFlows(B) {
    const nodes = [
        { id: 'asis', col: 0, label: 'As-is', color: 'var(--c-ink)', value: B.asis },
        { id: 'bu', col: 0, label: 'New hires (build-ups)', color: 'var(--c-pink)', value: B.d1BuildUps + B.tsaRepl + B.d2BuildUps },
        { id: 'cd1', col: 1, label: 'Carve-out Day 1', color: 'var(--c-violet)', value: B.day1 },
        { id: 'rd1', col: 1, label: 'RemainCo after Day 1', color: 'var(--c-primary)', value: B.remainOrg },
        { id: 'nlr', col: 1, label: 'No longer required', color: 'var(--c-danger)', value: B.nlr },
        { id: 'cd2', col: 2, label: 'Carve-out Day 2', color: 'var(--c-violet)', value: B.day2 },
        { id: 'exit', col: 2, label: 'Exits after Day 1', color: 'var(--c-danger)', value: B.rightsizing + B.remainRightsize },
        { id: 'rss', col: 2, label: 'RemainCo steady state', color: 'var(--c-primary)', value: B.remainOrg - B.remainRightsize },
    ];
    const links = [
        { s: 'asis', t: 'cd1', v: B.perimeter, label: 'Perimeter (dedicated workforce)' },
        { s: 'asis', t: 'cd1', v: B.transfers, label: 'Day 1 transfers' },
        { s: 'asis', t: 'rd1', v: B.remainOrg, label: 'Stays with RemainCo' },
        { s: 'asis', t: 'nlr', v: B.nlr, label: 'No longer required (at close)' },
        { s: 'bu', t: 'cd1', v: B.d1BuildUps, label: 'Day 1 build-ups' },
        { s: 'bu', t: 'cd2', v: B.tsaRepl + B.d2BuildUps, label: 'TSA replacements + Day 2 build-ups' },
        { s: 'cd1', t: 'cd2', v: B.day1 - B.rightsizing, label: 'Stays after Day 2' },
        { s: 'cd1', t: 'exit', v: B.rightsizing, label: 'Day 2 exits (rightsizing)' },
        { s: 'rd1', t: 'rss', v: B.remainOrg - B.remainRightsize, label: 'Stays in RemainCo' },
        { s: 'rd1', t: 'exit', v: B.remainRightsize, label: 'Stranded rightsizing' },
    ].filter((l) => l.v > SANKEY_EPSILON);
    return { nodes: nodes.filter((n) => n.value > SANKEY_EPSILON), links };
}
/** Shared accumulator: `push` walks a running total, so column order defines the arithmetic. */
function waterfall(start) {
    const cols = [];
    let run = start;
    return {
        push(label, val, color, total) {
            if (total) {
                cols.push({ label, lo: 0, hi: run, val: run, total: true, color });
                return;
            }
            cols.push({ label, lo: Math.min(run, run + val), hi: Math.max(run, run + val), val, color });
            run += val;
        },
        done: () => ({ cols, maxBar: Math.max(...cols.map((c) => c.hi)) }),
    };
}
/**
 * Carve-out waterfall: As-is → Perimeter → Day 1 → Day 2. Ported from phaseBridgeCols (:7753).
 *
 * The first step subtracts `remainOrg + transfers` together, which looks like a bug and is not:
 * transfers are still sitting in RemainCo at the perimeter cut and only move at Day 1, so they leave
 * here and come back two columns later.
 */
export function phaseBridgeCols(B) {
    const w = waterfall(B.asis);
    w.push('As-is', 0, 'ink', true);
    w.push('Stays w/ RemainCo', -(B.remainOrg + B.transfers), 'muted');
    w.push('No longer req.', -B.nlr, 'danger');
    w.push('Perimeter', 0, 'violet', true);
    w.push('Transfers', B.transfers, 'pink');
    w.push('Build-ups (D1)', B.d1BuildUps, 'pink');
    w.push('Day 1', 0, 'violet', true);
    w.push('Day 2 exits', -B.rightsizing, 'danger');
    w.push('TSA repl.', B.tsaRepl, 'pink');
    w.push('Build-ups (D2)', B.d2BuildUps, 'pink');
    w.push('Day 2', 0, 'violet', true);
    return w.done();
}
/**
 * RemainCo mirror: what the staying organisation looks like once the carve-out and its Day 1 transfers
 * have gone, then rightsized to steady state. Ported from remainMirrorCols (:7777).
 *
 * Redeployments are absent by design — they keep people employed, so they move cost around inside
 * RemainCo without changing its size.
 */
export function remainMirrorCols(B) {
    const w = waterfall(B.asis);
    w.push('As-is', 0, 'ink', true);
    w.push('Perimeter leaves', -B.perimeter, 'violet');
    w.push('No longer req.', -B.nlr, 'danger');
    w.push('Transfers leave (D1)', -B.transfers, 'pink');
    w.push('RemainCo after Day 1', 0, 'primary', true);
    w.push('Stranded rightsizing', -B.remainRightsize, 'danger');
    w.push('RemainCo steady state', 0, 'primary', true);
    return w.done();
}
export function priceScenario(state, design) {
    const hc = computeStageBridge(state, { design });
    const fte = computeStageBridge(state, { design, fn: FTE });
    const cost = computeStageBridge(state, { design, fn: salaryMetric(state.assumptions) });
    return {
        hc: { day1: hc.day1, day2: hc.day2 },
        fte: { day1: fte.day1, day2: fte.day2 },
        costDay1: cost.day1,
        oneOffTotal: computeOneOffCosts(state, { design }).total,
        strandedResidual: computeStrandedTimeline(state, { design }).residualRunRate,
    };
}
/**
 * One-off cost build-up: severance by population, then hiring, then retention, to the total.
 *
 * Every step is a cost, so the waterfall only climbs — what the chart adds over the one-off TABLE
 * is which populations the money reaches, which is the first question a CFO asks of the number.
 * Zero steps are omitted rather than drawn as empty columns: a project without a retention budget
 * gets a chart about the costs it has, not the features it has not used.
 */
export function oneOffCols(O) {
    const w = waterfall(0);
    if (O.nlrSeverance > 0)
        w.push('Severance — at close', O.nlrSeverance, 'danger');
    if (O.d2Severance > 0)
        w.push('Severance — Day 2 exits', O.d2Severance, 'danger');
    if (O.strandedSeverance > 0)
        w.push('Severance — RemainCo', O.strandedSeverance, 'danger');
    if (O.hiring > 0)
        w.push('Hiring', O.hiring, 'accent');
    if (O.retention > 0)
        w.push('Retention', O.retention, 'accent2');
    w.push('One-off total', 0, 'ink', true);
    return w.done();
}
/**
 * Where each as-is leaf group's people end up in the target structure.
 *
 * The denominators use the same filled-population rule as every other surface, so coverage cannot be
 * inflated by open positions, inactive people or attrition leavers. Build-ups have no as-is base, so
 * they land on the diagonal of their target group — which is why coverage can legitimately exceed
 * 100%.
 */
export function computeCoverageMatrix(state, options) {
    const { levels, perspective } = options;
    const fn = options.fn ?? (() => 1);
    const filters = options.filters ?? [];
    const bucket = PERSPECTIVE_BUCKET[perspective];
    /*
      One level's value, as a string, with ONLY an absent value folded into "(No …)".
  
      `value || '(No …)'` folded a numeric 0 as well, and the matrix's levels are not restricted to the
      hierarchy: `availableFields` offers dedication, salary and FTE, all stored as numbers. So somebody
      recorded at 0% dedication was filed with the people nobody had measured, the "0" row never appeared,
      and the "(No …)" row's percentage blended two populations — while pivoting the chart by the same
      field put them in a group called "0" (hierarchy.ts). Same field, same person, two groups in one tool.
    */
    const segmentOf = (p, l, scenario) => {
        const value = resolveField(state, p, l.fieldId, scenario);
        if (value === null || value === undefined || value === '')
            return `(No ${l.label})`;
        return String(value);
    };
    const segmentsOf = (p, scenario) => levels.map((l) => segmentOf(p, l, scenario));
    const asisN = Object.create(null);
    const cellNum = Object.create(null);
    const leafSet = new Set();
    const asisLeafSet = new Set();
    const segments = Object.create(null);
    const bump = (i, j, v) => {
        const row = cellNum[i] ?? (cellNum[i] = Object.create(null));
        row[j] = (row[j] ?? 0) + v;
    };
    for (const p of state.employees) {
        if (!countsInAggregates(p))
            continue;
        // The cohort is narrowed by as-is identity, so a filter means "these people", not "these targets".
        if (filters.length && !passesFilters(state, p, 'as-is', filters))
            continue;
        const asisSegments = segmentsOf(p, 'as-is');
        const ai = asisSegments.join(' › ');
        asisN[ai] = (asisN[ai] ?? 0) + fn(p);
        leafSet.add(ai);
        // Presence, independent of weight — see `asisLeaves`.
        asisLeafSet.add(ai);
        segments[ai] = asisSegments;
        if (currentBucket(state.design, p.id) === bucket) {
            const targetSegments = segmentsOf(p, perspective);
            const tj = targetSegments.join(' › ');
            leafSet.add(tj);
            segments[tj] = targetSegments;
            bump(ai, tj, fn(p));
        }
    }
    for (const b of state.design.buildUps) {
        if (!countsInHeadcount(b) || currentBucket(state.design, b.id) !== bucket)
            continue;
        if (filters.length && !passesFilters(state, b, perspective, filters))
            continue;
        const targetSegments = segmentsOf(b, perspective);
        const tj = targetSegments.join(' › ');
        leafSet.add(tj);
        segments[tj] = targetSegments;
        bump(tj, tj, fn(b));
    }
    return {
        leaves: [...leafSet].sort((a, b) => a.localeCompare(b)),
        asisN,
        cellNum,
        asisLeaves: [...asisLeafSet].sort((a, b) => a.localeCompare(b)),
        segments,
    };
}
/**
 * Coverage flags: a diagonal cell below LOW or above HIGH, an off-diagonal above HIGH.
 *
 * The app's thresholds (orgchart.html:7692), named here because a pane that inlined them would be the
 * only place the rule lived — and the rule is a judgement about what a reader should be made to look at,
 * not a rendering constant.
 */
export const COVERAGE_LOW = 10;
export const COVERAGE_HIGH = 80;
/**
 * The matrix as cells, classified — the whole of the app's inline render decision (orgchart.html:8176).
 *
 * In core because every one of those decisions is a claim about the data: which cells print a
 * percentage, which print an arrival, which are flagged for the reader, and how dark the shading is.
 * A pane that made them inline would be the app again.
 */
export function coverageCells(matrix, options = {}) {
    const low = options.low ?? COVERAGE_LOW;
    const high = options.high ?? COVERAGE_HIGH;
    const hasAsis = new Set(matrix.asisLeaves);
    return matrix.leaves.map((row) => matrix.leaves.map((col) => {
        const diagonal = row === col;
        const num = matrix.cellNum[row]?.[col] ?? 0;
        const denom = matrix.asisN[row] ?? 0;
        const pct = denom > 0 ? (num / denom) * 100 : null;
        let kind;
        if (pct !== null)
            kind = diagonal || num > 0 ? 'pct' : 'empty';
        else if (num > 0)
            kind = hasAsis.has(row) ? 'no-weight' : 'new';
        else
            kind = 'empty';
        const flagged = pct !== null && (diagonal ? pct <= low || pct >= high : pct >= high);
        return {
            row,
            col,
            diagonal,
            num,
            denom,
            pct,
            kind,
            flagged,
            intensity: pct === null ? 0 : Math.min(1, pct / 100) * 0.8,
        };
    }));
}
/**
 * Header runs: one row of spans per level, for the column headers and (transposed) the row headers.
 *
 * Identified by the SEGMENT TUPLE prefix. The app joined the prefix with a space and compared strings
 * (orgchart.html:8146), so a business unit "Ops" with function "Support Desk" and a business unit
 * "Ops Support" with function "Desk" both flattened to "Ops Support Desk": the two adjacent leaves were
 * drawn as one group spanning both columns, labelled "Support Desk", and the label "Desk" appeared
 * nowhere in the table.
 */
export function coverageHeaderRuns(matrix, levelCount) {
    const segmentsAt = (leaf) => matrix.segments[leaf] ?? [leaf];
    const samePrefix = (a, b, depth) => {
        const sa = segmentsAt(a);
        const sb = segmentsAt(b);
        for (let i = 0; i <= depth; i++)
            if (sa[i] !== sb[i])
                return false;
        return true;
    };
    const rows = [];
    for (let level = 0; level < levelCount; level++) {
        const runs = [];
        let start = 0;
        while (start < matrix.leaves.length) {
            let end = start + 1;
            while (end < matrix.leaves.length && samePrefix(matrix.leaves[start], matrix.leaves[end], level)) {
                end++;
            }
            runs.push({
                level,
                start,
                span: end - start,
                label: segmentsAt(matrix.leaves[start])[level] ?? '',
            });
            start = end;
        }
        rows.push(runs);
    }
    return rows;
}
/**
 * The matrix's default hierarchy: the project's own grouping levels.
 *
 * A function so the pane does not inline the seed and a test can pin it — the app's equivalent is a
 * lazy initialiser on a module global (orgchart.html:8094) that nothing ever re-derives.
 */
export function defaultCoverageLevels(state) {
    return state.groupingLevels.map((l) => ({ fieldId: l.key, label: l.label }));
}
/**
 * Which fields this matrix may be broken down by, and why that is not simply `availableFields`.
 *
 * TARGET LEVELS ARE EXCLUDED. `resolveField` resolves a `tlvl*` key from the target placement whatever
 * scenario is asked for, so for a target level the as-is leaf and the perspective leaf are the SAME
 * string for every person: every count lands on the diagonal and no off-diagonal cell can ever be
 * non-empty. The app offered them, and a 40-unit target org drew a 40×40 grid with 40 populated cells
 * under a corner reading "As-is → Carve-out" over a matrix containing no transition. A control that can
 * only produce a diagonal is not a control.
 */
export function coverageFieldChoices(state) {
    return availableFields(state).filter((f) => !isTargetLevelKey(f.id));
}
/**
 * Drop levels whose field no longer exists, and refresh the labels of those that do.
 *
 * The app's `_covLevels` is a module global written once and overwritten by a deep link, never
 * revalidated (orgchart.html:8094, :8418) — so the entity shortcut on a project with no Legal Entity
 * column set it to a field `availableFields` does not offer: no `<option>` matched, the control showed
 * "Division", and the matrix collapsed to one cell reading 100% labelled "(No Legal Entity)". Opening a
 * second project was the same failure by another route, with the first project's labels left behind.
 *
 * Returns what was dropped, so a surface can say so rather than silently changing what it is showing.
 */
export function reconcileCoverageLevels(state, levels) {
    const offered = new Map(coverageFieldChoices(state).map((f) => [f.id, f.label]));
    const kept = [];
    const dropped = [];
    for (const level of levels) {
        const label = offered.get(level.fieldId);
        if (label === undefined)
            dropped.push(level.label || level.fieldId);
        else
            kept.push({ fieldId: level.fieldId, label });
    }
    return { levels: kept.length ? kept : defaultCoverageLevels(state), dropped };
}
/**
 * The three perspectives of ONE scenario, side by side, and how each as-is group divides between them.
 *
 * THE ROW IS THE AS-IS GROUP, and the columns say how much of it went where — the same reading as the
 * coverage matrix, and the only one under which the three columns describe a single population. Grouping
 * each column by its own resolved value would put one person in three different rows.
 *
 * THE WEIGHTS RESOLVE THROUGH THEIR COLUMN, though, because a column IS a perspective: a carve-out
 * salary override belongs in the carve-out money and not in the as-is money. The app summed raw
 * `e.salary` in both the cards and the table, so a per-scenario salary edit moved the group card behind
 * the modal and nothing here.
 *
 * BLANKS FOLD, which is what makes the table reconcile with the cards above it. The app built its group
 * list with `.filter(Boolean)` and skipped blank keys inside the aggregation (orgchart.html:7490-7496),
 * so people with no value at that level were dropped from every column while the cards counted them:
 * 500 people and a table summing to 488, with nothing on the page accounting for the twelve.
 */
export function computePerspectiveComparison(state, options = {}) {
    const design = options.design ?? state.design;
    const level = options.levelKey != null
        ? (state.groupingLevels.find((l) => l.key === options.levelKey) ?? null)
        : null;
    const bucketOf = (id) => design.buckets[id] ?? 'Remain';
    // Attrition leavers count nowhere. Build-ups do not exist as-is, so they reach only the design columns.
    const everybody = [...state.employees, ...design.buildUps].filter((p) => !p.isLeaver);
    const COLUMNS = [
        { key: 'as-is', label: 'As-is', has: (p) => !p.isBuildUp },
        { key: 'remain', label: 'RemainCo', has: (p) => bucketOf(p.id) === 'Remain' },
        { key: 'carve-out', label: 'Carve-out', has: (p) => bucketOf(p.id) === 'Carve-out' },
    ];
    const summaries = COLUMNS.map(({ key, label, has }) => {
        const members = everybody.filter(has);
        const counted = members.filter(countsInHeadcount);
        const fte = resolvedMetricFn(design, key, 'fte', null);
        const cost = resolvedMetricFn(design, key, 'cost', state.assumptions);
        return {
            key,
            label,
            hc: counted.length,
            fte: counted.reduce((a, p) => a + fte(p), 0),
            salary: counted.reduce((a, p) => a + cost(p), 0),
            buildUps: counted.filter((p) => p.isBuildUp).length,
            openPositions: members.filter((p) => p.openPosition).length,
            inactive: members.filter((p) => p.inactive && !p.openPosition).length,
        };
    });
    const rows = [];
    const totals = { asis: 0, remain: 0, carveOut: 0, noLonger: 0 };
    if (level) {
        const byKey = new Map();
        const remainCost = resolvedMetricFn(design, 'remain', 'cost', state.assumptions);
        const carveCost = resolvedMetricFn(design, 'carve-out', 'cost', state.assumptions);
        const asisCost = resolvedMetricFn(design, 'as-is', 'cost', state.assumptions);
        for (const person of everybody) {
            if (!countsInHeadcount(person))
                continue;
            /*
              The as-is value, blanks folded. `(No <label>)` is the fold every other grouping surface uses; the
              app had none here, which is precisely how the twelve went missing.
            */
            const raw = person.levels[level.key];
            const key = String(raw ?? '').trim() || `(No ${level.label})`;
            let row = byKey.get(key);
            if (!row) {
                row = {
                    key,
                    asis: { hc: 0, salary: 0 },
                    remain: { hc: 0, salary: 0 },
                    carveOut: { hc: 0, salary: 0 },
                    noLonger: { hc: 0, salary: 0 },
                };
                byKey.set(key, row);
                rows.push(row);
            }
            if (!person.isBuildUp) {
                row.asis.hc++;
                row.asis.salary += asisCost(person);
                totals.asis++;
            }
            const bucket = bucketOf(person.id);
            if (bucket === 'Carve-out') {
                row.carveOut.hc++;
                row.carveOut.salary += carveCost(person);
                totals.carveOut++;
            }
            else if (bucket === 'No longer required') {
                row.noLonger.hc++;
                row.noLonger.salary += asisCost(person);
                totals.noLonger++;
            }
            else {
                row.remain.hc++;
                row.remain.salary += remainCost(person);
                totals.remain++;
            }
        }
        rows.sort((a, b) => b.asis.hc - a.asis.hc || a.key.localeCompare(b.key));
    }
    return {
        levelKey: level?.key ?? null,
        levelLabel: level?.label ?? null,
        summaries,
        rows,
        totals,
    };
}
/**
 * One grouping level's population, measured three ways — the "By <level>" analytics tabs.
 *
 * A compute function because the app had none: eighteen lines inside a render branch
 * (orgchart.html:7377-7392) plus two template helpers (:7395-7437), and every defect below survived
 * there because there was nothing a harness could hold.
 *
 * PERSPECTIVE, NOT A LENS. The signature takes a perspective and nothing else, so `viewMode`, the
 * phase-bar stage, the chart's filters and the pivot are all unreachable from here — by type, not by
 * discipline. The app intersected this pane with the chart's whole lens (:7291) while captioning it
 * with the perspective alone, so the same tab read 120 or 400 depending on a display-only toggle in a
 * toolbar behind the modal, both times captioned "Carve-out view".
 *
 * VALUES RESOLVE THROUGH THE PERSPECTIVE. The app grouped on the raw stored value (`e[lvl.key]`,
 * :7388) and summed raw `e.salary`/`e.fte` (:7397, :7412) while the group card beside it resolves all
 * three through `getEmpField`. So a per-scenario move, a group rename or a salary override changed the
 * card and not the table — same person, same perspective, two placements.
 */
export function computeLevelBreakdown(state, options) {
    const { levelKey, perspective } = options;
    const levels = state.groupingLevels;
    const idx = levels.findIndex((l) => l.key === levelKey);
    const level = idx >= 0 ? levels[idx] : { key: levelKey, label: levelKey };
    const ancestors = idx > 0 ? levels.slice(0, idx) : [];
    const bucket = PERSPECTIVE_BUCKET[perspective];
    const fte = resolvedMetricFn(state.design, perspective, 'fte', null);
    const cost = resolvedMetricFn(state.design, perspective, 'cost', state.assumptions);
    /*
      Build-ups do not exist as-is, which is what `computeStageBridge` says too — its As-is total comes
      from the employee loop alone. The app put them in every tab, so the default analytics tab and the
      summary tab beside it reported two different as-is headcounts with nothing to explain the gap.
    */
    const candidates = perspective === 'as-is'
        ? state.employees
        : [...state.employees, ...state.design.buildUps].filter((p) => {
            const b = currentBucket(state.design, p.id);
            if (b === bucket)
                return true;
            // Undecided EXISTING people stay in the Remain view: Remain is the default, not a decision.
            return perspective === 'remain' && !p.isBuildUp && state.design.buckets[p.id] === undefined;
        });
    const omitted = { openPositions: 0, inactive: 0, leavers: 0 };
    const counted = [];
    for (const p of candidates) {
        // A leaver is counted once, as a leaver, even when they are also an open position or inactive —
        // otherwise the three tallies sum to more than the people they describe.
        if (p.isLeaver)
            omitted.leavers++;
        else if (p.openPosition)
            omitted.openPositions++;
        else if (p.inactive)
            omitted.inactive++;
        else
            counted.push(p);
    }
    const label = (p, l) => String(resolveField(state, p, l.key, perspective) || '') || `(No ${l.label})`;
    const bucketed = new Map();
    const totals = { hc: 0, fte: 0, salary: 0, avgSalary: 0 };
    let buildUps = 0;
    for (const p of counted) {
        const ancestry = ancestors.map((l) => label(p, l));
        const value = label(p, level);
        const key = [...ancestry, value].join(' › ');
        let row = bucketed.get(key);
        if (!row) {
            row = { key, value, ancestry, hc: 0, fte: 0, salary: 0, avgSalary: 0, salaryShare: 0 };
            bucketed.set(key, row);
        }
        const f = fte(p);
        const s = cost(p);
        row.hc++;
        row.fte += f;
        row.salary += s;
        totals.hc++;
        totals.fte += f;
        totals.salary += s;
        if (p.isBuildUp)
            buildUps++;
    }
    totals.avgSalary = totals.hc ? totals.salary / totals.hc : 0;
    const rows = [...bucketed.values()];
    for (const r of rows) {
        r.avgSalary = r.hc ? r.salary / r.hc : 0;
        /*
          A share of the TOTAL, not of the largest row. The app's column is headed "Salary share" and drew
          `salary / maxSalary`, so the biggest group always filled the bar and a one-row table always read
          100% — three groups at 500k/500k/100k drew full, full, one-fifth for real shares of 45/45/9.
        */
        r.salaryShare = totals.salary ? r.salary / totals.salary : 0;
    }
    /*
      Salary, then headcount, then the key. The app sorted on salary alone, so a project imported without
      a salary column had every comparison return 0 and fell back to the order people happened to appear
      in the uploaded sheet — a 300-person function below a 2-person one, and a re-upload reordering the
      table with no change to the data.
    */
    rows.sort((a, b) => b.salary - a.salary || b.hc - a.hc || a.key.localeCompare(b.key));
    return {
        levelKey: level.key,
        levelLabel: level.label,
        perspective,
        rows,
        totals,
        omitted,
        buildUps,
        anySalary: totals.salary > 0,
    };
}
/** A person's target placement, as the ordered list of unit values they carry. */
export function targetPlacementValues(state, person) {
    const out = [];
    for (const level of state.targetLevels ?? []) {
        const v = getEmpField(person, level.key, 'target', state.targetOverrides);
        if (v == null || String(v).trim() === '')
            break;
        out.push(String(v));
    }
    return out;
}
/**
 * Every addressable target unit, sorted by label.
 *
 * A declared path implies its ancestors: declaring `['Operations','Field']` makes `Operations` a unit
 * too, whether or not it was declared separately. Expanding the prefixes here is what lets someone
 * be placed at any level of the blueprint, and the dedupe is what stops a unit declared both ways
 * appearing twice.
 */
export function targetUnitOptions(state) {
    const seen = new Set();
    const out = [];
    for (const unit of state.targetUnits ?? []) {
        for (let k = 0; k < unit.length; k++) {
            const path = unit.slice(0, k + 1);
            const key = JSON.stringify(path);
            if (seen.has(key))
                continue;
            seen.add(key);
            out.push({ value: key, label: path.join(' › ') });
        }
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
}
/**
 * Headcount, FTE and cost per declared target unit, counting a unit's whole branch.
 *
 * Rows are rounded exactly as the original does — this feeds a table and an Excel sheet, and a
 * "tidier" unrounded version would disagree with both.
 */
export function computeTargetRollup(state) {
    const units = targetUnitOptions(state);
    if (!units.length)
        return [];
    // Planned hires count here: a target unit's size is what it will be, so a Day 1/Day 2 build-up
    // placed into the blueprint belongs in its rollup alongside the transferred people.
    const carve = [...state.employees, ...state.design.buildUps].filter((p) => currentBucket(state.design, p.id) === 'Carve-out' && countsInAggregates(p));
    const placements = carve.map((p) => ({ p, path: targetPlacementValues(state, p) }));
    const inBranch = (unitPath, path) => path.length >= unitPath.length && unitPath.every((s, i) => path[i] === s);
    const agg = (members) => ({
        Headcount: members.length,
        FTE: Math.round(members.reduce((a, m) => a + (Number(m.p.fte) || 0), 0)),
        Cost: Math.round(members.reduce((a, m) => a + loadedCost(m.p, state.assumptions), 0)),
    });
    const rows = units.map((o) => {
        const path = JSON.parse(o.value);
        return { Unit: o.label, Level: path.length, ...agg(placements.filter((m) => inBranch(path, m.path))) };
    });
    rows.push({ Unit: '(Unplaced)', Level: '', ...agg(placements.filter((m) => m.path.length === 0)) });
    rows.push({ Unit: 'All carve-out', Level: '', ...agg(placements) });
    return rows;
}
export { groupNodeId, ROOT_ID };
//# sourceMappingURL=analytics.js.map